import { Spin } from 'lego-on-react';
import React, { ReactNode, useEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Resources';
import block from 'propmods';
import { useDebouncedCallback } from 'use-debounce';

import { formatBytes } from '../../lib/utils';
import { Quota } from '../../model/resources';
import { ItemsPage } from '../../types/utils';
import Pagination from '../Pagination/Pagination';
import Progress from '../Progress/Progress';
import { PS } from '../PS';

import './ResourceManager.scss';

const b = block('ResourceManager');
const t = i18n(keyset);

interface Props<T extends { id: string }> {
    skill: { id: string };
    type: 'sounds' | 'images';
    getList: (page: number) => Promise<ItemsPage<T>>;
    remove: (id: string) => Promise<void>;
    upload: (file: File) => Promise<T>;
    children: (props: { items: Array<ItemWrapper<T>>; remove: (id: string) => Promise<void> }) => React.ReactNode;
    getQuota: () => Promise<Quota>;
    accept?: string | string[];
    uploadText: ReactNode;
    disabled: boolean;
}

export interface ItemWrapper<T> {
    file?: File;
    item?: T;
    error?: ResourceError;
}

export interface ResourceError {
    message: string;
    payload?: {
        type?: string;
    };
}

function ResourceManager<T extends { id: string }>(props: Props<T>) {
    const { getList, getQuota, upload, children, accept, uploadText, disabled } = props;
    const [quota, setQuota] = useState<Quota>();
    const [limit, setLimit] = useState(0);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<Array<ItemWrapper<T>>>([]);
    const [isListLoading, setIsListLoading] = useState(true);

    const updateQuota = () => {
        getQuota().then(setQuota).catch(console.error);
    };

    const debouncedUpdateQuota = useDebouncedCallback(updateQuota, 1000);

    const loadAll = async (showLoader = true) => {
        try {
            if (showLoader) {
                setIsListLoading(true);
            }

            const itemsRes = await getList(page);
            setItems(itemsRes.items.map((x) => ({ item: x, isLoading: false })));
            setLimit(itemsRes.limit);
            setTotal(itemsRes.total);
        } catch (e) {
            console.error(e);
        } finally {
            setIsListLoading(false);
        }
    };

    const debouncedLoadAll = useDebouncedCallback(() => void loadAll(false), 1000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void loadAll(), [props.skill.id, props.type, page]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void updateQuota(), [props.skill.id, props.type]);

    const onDrop = (acceptedFiles: File[]) => {
        if (disabled) return;
        for (const file of acceptedFiles) {
            const wrp: ItemWrapper<T> = { file };
            setTotal(total + 1);
            upload(file)
                .then((res) => {
                    wrp.item = res;
                    setItems([...items]);
                    debouncedUpdateQuota();
                })
                .catch((e) => {
                    wrp.error = e;
                    setItems([...items]);
                });

            items.unshift(wrp);
        }

        setItems([...items]);
    };

    const remove = (id: string) => {
        setItems([...items.filter((x) => !x.item || x.item.id !== id)]);

        return props
            .remove(id)
            .then(() => {
                debouncedLoadAll();
                debouncedUpdateQuota();
            })
            .catch(console.error);
    };

    const pageCount = limit > 0 ? Math.ceil(total / limit) : 0;

    return (
        <div {...b()}>
            {isListLoading && (
                <div {...b('items-loader')}>
                    <Spin size={'l'} progress />
                </div>
            )}
            <div>
                <Progress
                    value={quota ? quota.quota.used : 0}
                    max={quota ? quota.quota.total : 0}
                    format={formatBytes}
                />
            </div>
            <div {...b('dropzone')}>
                <PS.DropZone accept={accept} onDrop={onDrop} uploadText={uploadText} size="l" disabled={disabled} />
            </div>
            {total > 0 ? (
                <div>{children({ items, remove })}</div>
            ) : (
                <div {...b('no-data')}>{t('error-not-loaded')}</div>
            )}
            <br />
            {total > 0 && <Pagination currentPage={page} pageCount={pageCount} onSelect={setPage} />}
        </div>
    );
}

export default ResourceManager;
